/**
 * Tags Service for Client-Side Operations
 * Handles all tag CRUD operations using Supabase directly
 * Matches the exact structure from server implementation
 */

import { supabase, isSupabaseConfigured, getCurrentSession } from "@/lib/supabase";
import type { Tag, InsertTag } from "@shared/schema";

class TagsService {
    /**
     * Get current user ID from centralized session (single auth call on load)
     */
    private async getCurrentUserId(): Promise<string> {
        if (!isSupabaseConfigured) {
            throw new Error("Service not configured. Please try again later.");
        }
        const session = await getCurrentSession();
        if (!session?.user) {
            throw new Error("User not authenticated. Please log in.");
        }
        return session.user.id;
    }

    /**
     * Get all tags for the current user
     */
    async getTags(): Promise<Tag[]> {
        const userId = await this.getCurrentUserId();
        
        const { data, error } = await supabase
            .from("tags")
            .select("id, user_id, name, color, description, category, created_at, updated_at")
            .eq("user_id", userId)
            .order("name", { ascending: true });

        if (error) {
            console.error("Error fetching tags:", error);
            throw new Error(`Failed to fetch tags: ${error.message}`);
        }

        return (data || []) as Tag[];
    }

    /**
     * Get a single tag by ID (for current user only)
     */
    async getTag(id: string): Promise<Tag | null> {
        const userId = await this.getCurrentUserId();
        
        const { data, error } = await supabase
            .from("tags")
            .select("id, user_id, name, color, description, category, created_at, updated_at")
            .eq("id", id)
            .eq("user_id", userId)
            .single();

        if (error) {
            if (error.code === "PGRST116") {
                // No rows returned
                return null;
            }
            console.error("Error fetching tag:", error);
            throw new Error(`Failed to fetch tag: ${error.message}`);
        }

        if (!data) return null;

        return data as Tag;
    }

    /**
     * Get a tag by name (for current user only)
     * Case-insensitive search
     */
    async getTagByName(name: string): Promise<Tag | null> {
        const userId = await this.getCurrentUserId();
        
        // Get all tags for user and filter case-insensitively
        const { data, error } = await supabase
            .from("tags")
            .select("id, user_id, name, color, description, category, created_at, updated_at")
            .eq("user_id", userId);

        if (error) {
            console.error("Error fetching tags by name:", error);
            throw new Error(`Failed to fetch tag: ${error.message}`);
        }

        if (!data || data.length === 0) {
            return null;
        }

        // Case-insensitive search
        const found = data.find(tag => tag.name.toLowerCase() === name.toLowerCase());
        return (found || null) as Tag | null;
    }

    /**
     * Create a new tag
     * Checks if tag with same name already exists for the user
     */
    async createTag(tagData: Omit<InsertTag, "userId">): Promise<Tag> {
        const userId = await this.getCurrentUserId();
        
        // Check if tag already exists
        const existing = await this.getTagByName(tagData.name);
        if (existing) {
            throw new Error("Tag with this name already exists");
        }

        const tagToInsert: any = {
            user_id: userId,
            name: tagData.name.trim(),
            color: tagData.color || null,
            description: tagData.description || null,
            category: tagData.category || null,
        };

        const { data, error } = await supabase
            .from("tags")
            .insert(tagToInsert)
            .select()
            .single();

        if (error) {
            console.error("Error creating tag:", error);
            throw new Error(`Failed to create tag: ${error.message}`);
        }

        if (!data) {
            throw new Error("Failed to create tag: No data returned");
        }

        return data as Tag;
    }

    /**
     * Update an existing tag (for current user only)
     */
    async updateTag(
        id: string,
        updates: Partial<Omit<InsertTag, "userId">>
    ): Promise<Tag | null> {
        const userId = await this.getCurrentUserId();
        
        // First, verify the tag exists and belongs to the user
        const existing = await this.getTag(id);
        if (!existing) {
            return null;
        }

        // If name is being updated, check for duplicates
        if (updates.name && updates.name !== existing.name) {
            const duplicate = await this.getTagByName(updates.name);
            if (duplicate && duplicate.id !== id) {
                throw new Error("Tag with this name already exists");
            }
        }

        const updateData: any = {
            updated_at: new Date().toISOString(),
        };

        if (updates.name !== undefined) updateData.name = updates.name.trim();
        if (updates.color !== undefined) updateData.color = updates.color;
        if (updates.description !== undefined) updateData.description = updates.description;
        if (updates.category !== undefined) updateData.category = updates.category;

        const { data, error } = await supabase
            .from("tags")
            .update(updateData)
            .eq("id", id)
            .eq("user_id", userId)
            .select()
            .single();

        if (error) {
            if (error.code === "PGRST116") {
                return null;
            }
            console.error("Error updating tag:", error);
            throw new Error(`Failed to update tag: ${error.message}`);
        }

        if (!data) return null;

        return data as Tag;
    }

    /**
     * Delete a tag (for current user only)
     */
    async deleteTag(id: string): Promise<boolean> {
        const userId = await this.getCurrentUserId();
        
        // First, verify the tag exists and belongs to the user
        const existing = await this.getTag(id);
        if (!existing) {
            return false;
        }

        // Delete tag entity links first (if they exist in a separate table)
        // Note: Supabase will handle CASCADE if foreign keys are set up correctly
        const { error } = await supabase
            .from("tags")
            .delete()
            .eq("id", id)
            .eq("user_id", userId);

        if (error) {
            console.error("Error deleting tag:", error);
            throw new Error(`Failed to delete tag: ${error.message}`);
        }

        return true;
    }
}

/**
 * Create and export a singleton instance
 */
export const tagsService = new TagsService();
